import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePublicAnonymizedTalent } from "../../hooks/useAnonymizedTalents";
import { usePublicLanguageSwitch } from "../../hooks/usePublicLanguageSwitch";
import { AnonymizedTalentFichePremium } from "../../components/anonymizedTalent/AnonymizedTalentFichePremium";
import { PublicLanguageSwitcher } from "../../components/anonymizedTalent/PublicLanguageSwitcher";
import { PublicAnonymizedTalentRateLimitedError } from "../../api/anonymized-talents-api";
import { brandFontFamily, zoullooColors } from "../../theme";

/**
 * Public landing reached by prospects via the per-talent token URL.
 *
 *   /p/talents/:token?lang=fr
 *   /p/talents/:token?lang=en
 *
 * Mounted outside the authenticated app shell — must work without any user
 * session. Renders the V5.1 "premium fiche" design when the link is active,
 * a friendly "expired / not found" panel otherwise, and a dedicated "trop de
 * requêtes" panel when the backend's rate limiter rejects the call.
 *
 * Language: picked up from the `lang=` query parameter so the CSM can copy
 * the URL in the right language for their prospect. Defaults to French. The
 * `lang` is forwarded to the API so translatable fields (Title, Presentation,
 * JobTitle, MissionDescription, …) come back in the visitor's language.
 *
 * SEO: we set a runtime <meta name="robots" content="noindex, nofollow"> so
 * search engines don't index prospect-share URLs (the token is meant to stay
 * within the recipient's inbox).
 */
const PublicAnonymizedTalent: React.FC = () => {
	const { token } = useParams<{ token: string }>();
	const { language, setLanguage } = usePublicLanguageSwitch();
	const { data: talent, isLoading, error } = usePublicAnonymizedTalent(
		token,
		language
	);

	// Inject noindex meta tag while this page is mounted; clean up on unmount
	// so the rest of the app (which is indexable in principle) is unaffected.
	useEffect(() => {
		const meta = document.createElement("meta");
		meta.name = "robots";
		meta.content = "noindex, nofollow";
		document.head.appendChild(meta);
		return () => {
			document.head.removeChild(meta);
		};
	}, []);

	if (isLoading) {
		return (
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					backgroundColor: zoullooColors.brand.midnight
				}}
			>
				<CircularProgress sx={{ color: zoullooColors.brand.coquelicot }} />
			</Box>
		);
	}

	if (error instanceof PublicAnonymizedTalentRateLimitedError) {
		return <RateLimitedPanel />;
	}

	if (error || !talent) {
		return <UnavailableLinkPanel />;
	}

	return (
		<AnonymizedTalentFichePremium
			talent={talent}
			languageSwitcher={
				<PublicLanguageSwitcher language={language} onChange={setLanguage} />
			}
		/>
	);
};

/**
 * Shared layout for the "branded status" panels (unavailable link / rate limited).
 * Keeps the Zoulloo branding visible so the prospect doesn't think the link is broken.
 */
const StatusPanel: React.FC<{
	title: string;
	message: string;
}> = ({ title, message }) => (
	<Box
		sx={{
			minHeight: "100vh",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 2,
			backgroundColor: zoullooColors.brand.midnight,
			color: "white",
			padding: 4,
			textAlign: "center",
			fontFamily: brandFontFamily
		}}
	>
		<Typography
			sx={{
				fontSize: 56,
				fontWeight: 800,
				letterSpacing: -2,
				lineHeight: 1
			}}
		>
			zoull
			<span style={{ color: zoullooColors.brand.coquelicot }}>oo</span>
		</Typography>
		<Typography sx={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>
			{title}
		</Typography>
		<Typography sx={{ fontSize: 14, opacity: 0.7, maxWidth: 460 }}>
			{message}
		</Typography>
	</Box>
);

/**
 * Single "this link is no longer available" panel. Branded but minimal so
 * prospects who hit an expired / revoked / typo'd token still see a clean page.
 */
const UnavailableLinkPanel: React.FC = () => {
	const { t } = useTranslation();
	return (
		<StatusPanel
			title={t("anonymizedTalent.linkUnavailableTitle")}
			message={t("anonymizedTalent.linkUnavailableMessage")}
		/>
	);
};

/**
 * Shown when the backend rate-limits us (60 req/min/IP). The token is most
 * likely valid — the visitor just hit too many requests recently. A friendlier
 * message than the generic "unavailable" makes it obvious that retrying soon
 * will work.
 */
const RateLimitedPanel: React.FC = () => {
	const { t } = useTranslation();
	return (
		<StatusPanel
			title={t("anonymizedTalent.rateLimitedTitle")}
			message={t("anonymizedTalent.rateLimitedMessage")}
		/>
	);
};

export default PublicAnonymizedTalent;
